'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Environment } from '../../constants/environments';
import { HttpVerb } from '../../constants/httpVerbs';
import { User } from '../../types/user';
import { logger } from '../../utils/logger';
import { useApiService } from '../useApiService';

const RESALE_USER_STORAGE_KEY = 'alchemist_resale_user';
const RESALE_USER_ID_KEY = 'alchemist_resale_user_id';
const USER_SESSION_DURATION = 1000 * 60 * 60 * 2; // 2 hours

type ResponseDataUser = {
  responseData: User;
};

export type ResaleUserContextProps = {
  user: User | null;
  loading: boolean;
  setUser: (user: User | null, token?: string) => void;
  clearUser: () => void;
  refreshUser: () => Promise<void>;
};

const ResaleUserContext = createContext({} as ResaleUserContextProps);

/**
 * ResaleUserProvider - Manages user authentication specifically for the resale flow
 *
 * Single Responsibility: User management for resale transactions
 *
 * Features:
 * - Persists user data in localStorage (fallback when cookies fail)
 * - Auto-loads user from storage on mount
 * - Auto-expires user session after 2 hours
 * - Provides methods to set/clear/refresh user
 */
function ResaleUserProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const { request } = useApiService();

  /**
   * Clear user from state and localStorage
   */
  const clearUser = useCallback(() => {
    setUserState(null);
    sessionStorage.removeItem(RESALE_USER_STORAGE_KEY);
    sessionStorage.removeItem(RESALE_USER_ID_KEY);
  }, []);

  /**
   * Load user data from sessionStorage if available and not expired
   */
  const loadUserFromStorage = useCallback(() => {
    try {
      const storedUser = sessionStorage.getItem(RESALE_USER_STORAGE_KEY);
      const storedUserId = sessionStorage.getItem(RESALE_USER_ID_KEY);

      if (!storedUser || !storedUserId) {
        setLoading(false);
        return;
      }

      const userData: User & { lastUpdated?: number } = JSON.parse(storedUser);

      // Check if user session has expired
      if (userData.lastUpdated) {
        const now = Date.now();
        const sessionAge = now - userData.lastUpdated;

        if (sessionAge > USER_SESSION_DURATION) {
          logger.warn('[ResaleUser] User session expired, clearing storage');
          clearUser();
          setLoading(false);
          return;
        }
      }

      setUserState(userData);
    } catch (error) {
      logger.error('[ResaleUser] Failed to load user from storage');
      clearUser();
    } finally {
      setLoading(false);
    }
  }, [clearUser]);

  /**
   * Load user from sessionStorage on mount
   */
  useEffect(() => {
    loadUserFromStorage();
  }, [loadUserFromStorage]);

  /**
   * Set user and persist to sessionStorage
   * @param userData - User object to store (token included in object)
   * @param token - Optional token override (takes precedence over userData.token)
   */
  const setUser = (userData: User | null, token?: string) => {
    if (!userData) {
      clearUser();
      return;
    }

    try {
      // Add timestamp for session expiry; use provided token override if available
      const userWithTimestamp = {
        ...userData,
        ...(token ? { token } : {}),
        lastUpdated : Date.now(),
      };

      setUserState(userWithTimestamp);

      // Persist to sessionStorage (clears on tab close, shorter exposure than localStorage)
      sessionStorage.setItem(
        RESALE_USER_STORAGE_KEY,
        JSON.stringify(userWithTimestamp)
      );

      sessionStorage.setItem(RESALE_USER_ID_KEY, String(userData.id));
    } catch (error) {
      logger.error('[ResaleUser] Failed to store user');
    }
  };

  /**
   * Refresh user data from API
   * Uses token and userId stored inside the user object in sessionStorage
   */
  const refreshUser = async (): Promise<void> => {
    const storedUserRaw = sessionStorage.getItem(RESALE_USER_STORAGE_KEY);
    const storedUserId = sessionStorage.getItem(RESALE_USER_ID_KEY);

    if (!storedUserRaw || !storedUserId) {
      logger.warn('[ResaleUser] Cannot refresh user - no stored credentials');
      return;
    }

    let storedToken: string | undefined;
    try {
      const parsedUser: User = JSON.parse(storedUserRaw);
      storedToken = parsedUser.token;
    } catch {
      logger.warn('[ResaleUser] Cannot refresh user - failed to parse stored user');
      return;
    }

    if (!storedToken) {
      logger.warn('[ResaleUser] Cannot refresh user - no token in stored user');
      return;
    }

    try {
      setLoading(true);

      const resourcePath = `users/${storedUserId}?apikey=${Environment.API_KEY}&usertoken=${storedToken}`;

      const { responseData } = await request<null, ResponseDataUser>(
        {
          requestOptions : { resourcePath },
          type : HttpVerb.GET,
        },
        { baseUrl : Environment.API_URL }
      );

      if (responseData) {
        setUser(responseData, storedToken);
      }
    } catch (error) {
      logger.error('[ResaleUser] Failed to refresh user');
      // Don't clear user on refresh failure - they might be offline
    } finally {
      setLoading(false);
    }
  };

  const shared = {
    user,
    loading,
    setUser,
    clearUser,
    refreshUser,
  };

  return (
    <ResaleUserContext.Provider value={shared}>
      {children}
    </ResaleUserContext.Provider>
  );
}

ResaleUserProvider.displayName = 'ResaleUserProvider';

export const useResaleUserContext = (): ResaleUserContextProps =>
  useContext(ResaleUserContext);

export { ResaleUserContext, ResaleUserProvider };
